import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Briefcase, Calendar, Bell, User, MessageSquare,
  Video, FileText, Search, ClipboardList, BookOpen, Scale
} from 'lucide-react';

// قائمة التنقل السفلية للجوال - تظهر فقط على الشاشات الصغيرة
export default function MobileBottomNav({ userRole = 'client', unreadNotifications = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // إخفاء القائمة عند التمرير للأسفل وإظهارها عند التمرير للأعلى
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // تحديد عناصر القائمة حسب نوع المستخدم
  const getNavItems = () => {
    // العناصر الأساسية المشتركة
    const commonItems = [
      { id: 'home', icon: Home, label: 'الرئيسية', path: '/dashboard' },
      { id: 'notifications', icon: Bell, label: 'التنبيهات', path: '/notifications', badge: unreadNotifications },
    ];

    // للموظفين والمحامين
    if (['admin', 'lawyer', 'staff', 'employee'].includes(userRole)) {
      return [
        { id: 'home', icon: Home, label: 'الرئيسية', path: '/dashboard' },
        { id: 'tasks', icon: ClipboardList, label: 'المهام', path: '/my-tasks' },
        { id: 'attendance', icon: Calendar, label: 'الحضور و الانصراف', path: '/attendance' },
        { id: 'meetings', icon: Video, label: 'اجتماعات', path: '/video-meetings' },
        { id: 'notifications', icon: Bell, label: 'التنبيهات', path: '/notifications', badge: unreadNotifications },
      ];
    }

    //للشركاء
    if (['partner'].includes(userRole)) {
      return [
        { id: 'home', icon: Home, label: 'الرئيسية', path: '/dashboard' },
        { id: 'tasks', icon: ClipboardList, label: 'المهام', path: '/my-tasks' },
        { id: 'requests', icon: FileText, label: 'طلبات العملاء', path: '/partner/tasks' },
        { id: 'wallet', icon: Video, label: 'المحفظة', path: '/partner/wallet' },
        { id: 'notifications', icon: Bell, label: 'التنبيهات', path: '/partner-notifications', badge: unreadNotifications },
      ];
    }
    
    //للمسوقين
    if (['marketer'].includes(userRole)) {
      return [
        { id: 'home', icon: Home, label: 'الرئيسية', path: '/dashboard' },
        { id: 'appointments', icon: ClipboardList, label: 'المواعيد', path: '/marketer-appointments' },
        { id: 'meetings', icon: Video, label: 'اجتماعات', path: '/meetings' },
        { id: 'attendance', icon: Calendar, label: 'الحضور و الانصراف', path: '/attendance' },
        { id: 'notifications', icon: Bell, label: 'التنبيهات', path: '/notifications', badge: unreadNotifications },
      ];
    }

    // للعملاء الأفراد
    if (['individual', 'client'].includes(userRole)) {
      return [
        { id: 'home', icon: Home, label: 'الرئيسية', path: '/dashboard' },
        { id: 'profile', icon: Briefcase, label: 'ملفي الشامل', onClick: 'my-profile' },
        { id: 'requests', icon: FileText, label: 'طلباتي', path: '/escrow' },
        { id: 'meetings', icon: Video, label: 'اجتماعات', onClick: 'meetings' },
        { id: 'notifications', icon: Bell, label: 'التنبيهات', path: '/notifications', badge: unreadNotifications },
      ];
    }

    // للشركات
    if (['company', 'enterprise'].includes(userRole)) {
      return [
        { id: 'home', icon: Home, label: 'الرئيسية', path: '/dashboard' },
        { id: 'profile', icon: Briefcase, label: 'ملفي الشامل', onClick: 'my-profile' },
        { id: 'requests', icon: FileText, label: 'الطلبات', path: '/escrow' },
        { id: 'meetings', icon: Video, label: 'اجتماعات', onClick: 'meetings' },
        { id: 'notifications', icon: Bell, label: 'التنبيهات', path: '/notifications', badge: unreadNotifications },
      ];
    }

    return commonItems;
  };

  const navItems = getNavItems();
  
  // لا تظهر القائمة في الصفحة الرئيسية (Landing Page)
  if (location.pathname === '/') return null;

  // التحقق من المسار النشط
  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return location.pathname === path;
  };

  return (
    <>
      {/* مساحة فارغة لتجنب تداخل المحتوى مع القائمة */}
      <div className="lg:hidden h-20" />
      
      {/* القائمة السفلية */}
      <nav 
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 shadow-lg transition-transform duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        dir="rtl"
        data-testid="mobile-bottom-nav"
      >
        {/* Safe area للأجهزة ذات النوتش */}
        <div className="safe-area-bottom">
          <div className="flex items-center justify-around px-2 py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.onClick) {
                      if (location.pathname === '/dashboard') {
                        window.dispatchEvent(new CustomEvent('mobileNavViewChange', { detail: { view: item.onClick } }));
                      } else {
                        navigate('/dashboard', { state: { activeView: item.onClick } });
                      }
                    } else {
                      navigate(item.path);
                    }
                  }}
                  className={`relative flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-xl transition-all duration-200 ${
                    active 
                      ? 'text-gold bg-gold/10 scale-105' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gold hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {/* الأيقونة */}
                  <div className="relative">
                    <Icon className={`w-6 h-6 ${active ? 'stroke-[2.5]' : ''}`} />
                    
                    {/* شارة العدد */}
                    {item.badge > 0 && (
                      <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </div>
                  
                  {/* النص */}
                  <span className={`text-[10px] mt-1 font-medium ${active ? 'font-bold' : ''}`}>
                    {item.label}
                  </span>
                  
                  {/* مؤشر النشاط */}
                  {active && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-gold rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
